/* ═══════════════════════════════════════════════════════════════════════
   EF Collection Grid v2 — JS
   • IIFE, no globals
   • AJAX filter/sort/paginate via Shopify Section Rendering API
   • Filter drawer with focus trap + Esc + scroll-lock
   • localStorage wishlist
   • Density toggle (3/4 col on ≥1200px)
   • AR: hands off to existing window.efShowAR
   ═══════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var WISHLIST_KEY = 'ef-wishlist';
  var roots = document.querySelectorAll('[data-ef-cv2]');
  if (!roots.length) return;

  /* Wishlist (shared across all roots) */
  function readWishlist() {
    try { return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]'); }
    catch (e) { return []; }
  }
  function writeWishlist(arr) {
    try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(arr)); } catch (e) {}
  }
  function paintWishlist(scope) {
    var wishlist = readWishlist();
    scope.querySelectorAll('[data-ef-wishlist]').forEach(function (btn) {
      var handle = btn.getAttribute('data-product-handle');
      btn.classList.toggle('is-on', wishlist.indexOf(handle) !== -1);
      btn.setAttribute('aria-pressed', wishlist.indexOf(handle) !== -1 ? 'true' : 'false');
    });
  }

  roots.forEach(initRoot);

  function initRoot(root) {
    var sectionId = root.getAttribute('data-section-id');
    var collectionUrl = root.getAttribute('data-collection-url');
    var grid = root.querySelector('[data-ef-cv2-grid]');
    var bar = root.querySelector('[data-ef-cv2-bar]');
    var skeletonTpl = root.querySelector('[data-ef-cv2-skeleton]');

    /* State */
    var state = {
      tags: (root.getAttribute('data-current-tags') || '').split(',').filter(Boolean),
      sort: root.getAttribute('data-current-sort') || 'best-selling',
      priceMin: null,
      priceMax: null
    };

    /* ─── Sticky bar elevation — toggle .is-stuck when sentinel scrolls offscreen ─── */
    var sentinel = root.querySelector('[data-ef-bar-sentinel]');
    if (sentinel && bar && 'IntersectionObserver' in window) {
      var stuckObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          bar.classList.toggle('is-stuck', e.intersectionRatio === 0);
        });
      }, { threshold: [0, 1], rootMargin: '0px' });
      stuckObserver.observe(sentinel);
    }

    /* ─── Mobile scroll-aware "Back to..." crumb — auto-hide on scroll-down,
       reveal on scroll-up. Medium nav pattern. Only fires on subcategory
       pages (where the crumb element exists in the bar). 2026-05-07. ─── */
    if (bar && bar.querySelector('.ef-cv2__crumb--in-bar')) {
      var lastY = window.scrollY || 0;
      var ticking = false;
      var DELTA = 6; /* ignore micro-scrolls */
      var TOP_THRESHOLD = 80; /* always show when near top */
      function onScroll() {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          var y = window.scrollY || 0;
          var dy = y - lastY;
          if (Math.abs(dy) > DELTA) {
            if (y < TOP_THRESHOLD) {
              bar.classList.remove('is-scrolled-down');
            } else if (dy > 0) {
              bar.classList.add('is-scrolled-down');
            } else {
              bar.classList.remove('is-scrolled-down');
            }
            lastY = y;
          }
          ticking = false;
        });
      }
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    /* ─── Pill scroll arrows — desktop hover affordance for non-trackpad mice ─── */
    function setupPillScroll(scroller) {
      if (!scroller) return;
      /* Skip empty scrollers — wrapping would still take flex space and steal
         width from the populated wrap. Also skip if all children are hidden. */
      if (!scroller.children || scroller.children.length === 0) return;
      var hasVisiblePill = false;
      for (var i = 0; i < scroller.children.length; i++) {
        var c = scroller.children[i];
        if (c.offsetParent !== null || c.getClientRects().length > 0) { hasVisiblePill = true; break; }
      }
      if (!hasVisiblePill) return;
      // Wrap scroller with relatively-positioned parent so arrows can position absolute
      var wrap = document.createElement('div');
      wrap.className = 'ef-cv2__pills-wrap';
      // 2026-05-16 — Modifier class so CSS can distinguish primary/secondary/sibstrip wraps
      if (scroller.classList.contains('ef-cv2__pills--primary')) {
        wrap.classList.add('ef-cv2__pills-wrap--primary');
      } else if (scroller.classList.contains('ef-cv2__pills--secondary')) {
        wrap.classList.add('ef-cv2__pills-wrap--secondary');
      } else if (scroller.classList.contains('ef-cv2__sibstrip')) {
        wrap.classList.add('ef-cv2__pills-wrap--sibstrip');
      }
      scroller.parentNode.insertBefore(wrap, scroller);
      wrap.appendChild(scroller);

      var btnLeft = document.createElement('button');
      btnLeft.type = 'button';
      btnLeft.className = 'ef-cv2__pills-arrow ef-cv2__pills-arrow--left';
      btnLeft.setAttribute('aria-label', 'Scroll filters left');
      btnLeft.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>';

      var btnRight = document.createElement('button');
      btnRight.type = 'button';
      btnRight.className = 'ef-cv2__pills-arrow ef-cv2__pills-arrow--right';
      btnRight.setAttribute('aria-label', 'Scroll filters right');
      btnRight.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>';

      wrap.appendChild(btnLeft);
      wrap.appendChild(btnRight);

      function updateArrows() {
        /* 2026-05-16 — Show arrows ONLY when there's real overflow (content wider
           than container). No more "always show with 3+ pills" — that produced
           visual noise on pages with few pills that fit comfortably. */
        var overflow = scroller.scrollWidth - scroller.clientWidth;
        var canScroll = overflow > 8;
        wrap.classList.toggle('has-overflow', canScroll);
        if (!canScroll) return;
        btnLeft.classList.toggle('is-disabled', scroller.scrollLeft <= 2);
        btnRight.classList.toggle('is-disabled', scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 2);
      }

      /* rAF-debounced update — multiple resize/scroll events collapse to one repaint */
      var rafId = null;
      function scheduleUpdate() {
        if (rafId) return;
        rafId = requestAnimationFrame(function () { rafId = null; updateArrows(); });
      }

      btnLeft.addEventListener('click', function () { scroller.scrollBy({ left: -220, behavior: 'smooth' }); });
      btnRight.addEventListener('click', function () { scroller.scrollBy({ left: 220, behavior: 'smooth' }); });
      scroller.addEventListener('scroll', scheduleUpdate, { passive: true });
      window.addEventListener('resize', scheduleUpdate);

      /* ResizeObserver — catches layout shifts (trust band show/hide, font load, etc.)
         that change clientWidth without firing window resize. */
      if (typeof ResizeObserver !== 'undefined') {
        try { new ResizeObserver(scheduleUpdate).observe(scroller); } catch (e) {}
        try { new ResizeObserver(scheduleUpdate).observe(wrap); } catch (e) {}
      }

      /* Initial measure after fonts settle (pill widths depend on font load) */
      updateArrows();
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(updateArrows).catch(function(){});
      }
      setTimeout(updateArrows, 300);
      setTimeout(updateArrows, 1000);
    }

    /* 2026-05-16 v3 — also setup scroll arrows on primary pills (umbrella pages
       like /collections/fireplace-screens where 6 type pills can overflow). */
    root.querySelectorAll('.ef-cv2__pills--primary, .ef-cv2__pills--secondary, .ef-cv2__sibstrip').forEach(setupPillScroll);

    /* ─── Wishlist init ─── */
    paintWishlist(root);
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-ef-wishlist]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var handle = btn.getAttribute('data-product-handle');
      var wishlist = readWishlist();
      var idx = wishlist.indexOf(handle);
      if (idx === -1) wishlist.push(handle); else wishlist.splice(idx, 1);
      writeWishlist(wishlist);
      paintWishlist(root);
    });

    /* ─── Density toggle (3/4 col) ─── */
    root.querySelectorAll('[data-ef-density]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var d = btn.getAttribute('data-ef-density');
        root.querySelectorAll('[data-ef-density]').forEach(function (b) {
          var on = b === btn;
          b.classList.toggle('is-on', on);
          b.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        var g = root.querySelector('[data-ef-cv2-grid]');
        if (g) g.setAttribute('data-density', d);
      });
    });

    /* Snapshot primary pill tag pool — primary is single-select within its group,
       but secondary refinements (e.g. quick-ship) should persist across primary changes. */
    function primaryTagPool() {
      var arr = [];
      root.querySelectorAll('[data-ef-pill="primary"]').forEach(function (p) {
        var t = p.getAttribute('data-tag');
        if (t) arr.push(t);
      });
      return arr;
    }

    /* ─── Progressive disclosure map ───
       Reads embedded JSON `data-ef-cv2-disclosure` and hides/shows secondary pills
       based on the active primary category. Active selections persist if still valid. */
    var disclosureMap = {};
    var discEl = root.querySelector('[data-ef-cv2-disclosure]');
    if (discEl) {
      try { disclosureMap = JSON.parse(discEl.textContent.trim()); } catch (e) { disclosureMap = {}; }
    }

    var collectionsMap = {};
    var colMapEl = root.querySelector('[data-ef-cv2-collections]');
    if (colMapEl) {
      try { collectionsMap = JSON.parse(colMapEl.textContent.trim()); } catch (e) { collectionsMap = {}; }
    }
    var activeCategory = root.getAttribute('data-active-category') || 'all';

    function currentCategory() {
      var pool = primaryTagPool();
      for (var i = 0; i < pool.length; i++) {
        if (state.tags.indexOf(pool[i]) !== -1) return pool[i];
      }
      return activeCategory;
    }
    function applyDisclosure(category, animate) {
      if (!disclosureMap || !disclosureMap[category]) return;
      var allowed = disclosureMap[category];
      var secRow = root.querySelector('.ef-cv2__pills--secondary');
      if (!secRow) return;
      function swap() {
        var anyVisible = false;
        secRow.querySelectorAll('[data-ef-pill="secondary"]').forEach(function (p) {
          var tag = (p.getAttribute('data-tag') || '').toLowerCase();
          var show = allowed.indexOf(tag) !== -1;
          p.style.display = show ? '' : 'none';
          if (show) anyVisible = true;
        });
        secRow.style.display = anyVisible ? '' : 'none';
        /* Drop any active state.tags that are secondary refinements no longer visible */
        var primaryPool = primaryTagPool();
        state.tags = state.tags.filter(function (t) {
          if (primaryPool.indexOf(t) !== -1) return true; /* keep primary */
          return allowed.indexOf(t) !== -1;               /* keep only allowed secondary */
        });
      }
      if (animate) {
        secRow.classList.add('is-fading');
        setTimeout(function () {
          swap();
          secRow.classList.remove('is-fading');
        }, 150);
      } else {
        swap();
      }
    }
    /* Initial disclosure pass — hide pills that don't match the current category on page load */
    applyDisclosure(currentCategory(), false);
    applyConstraints();

    /* ─── Primary pills (single-select within primary group) ─── */
    root.addEventListener('click', function (e) {
      var pill = e.target.closest('[data-ef-pill="primary"]');
      if (!pill) return;
      var tag = pill.getAttribute('data-tag') || '';
      var pool = primaryTagPool();
      state.tags = state.tags.filter(function (t) { return pool.indexOf(t) === -1; });
      if (tag) state.tags.push(tag);
      applyDisclosure(tag || 'all', true);
      ajaxLoad();
    });

    /* ─── Pill constraints ─── */
    var constraintMap = {};
    var constraintEl = root.querySelector('[data-ef-cv2-constraints]');
    if (constraintEl) {
      try { constraintMap = JSON.parse(constraintEl.textContent); } catch (e) {}
    }

    function getDisabledTags() {
      var disabled = {};
      state.tags.forEach(function (activeTag) {
        var blocks = constraintMap[activeTag];
        if (blocks) {
          blocks.forEach(function (t) { disabled[t] = true; });
        }
      });
      return disabled;
    }

    /* ─── Dynamic tag co-occurrence (count-based disabling) ───
       Fetch all collection products once and build a tag → Set(tags) co-occurrence
       map. When the user has active filters, any pill whose tag never co-occurs
       with ALL active tags in any product gets disabled (would yield 0 results).
       Skips disabling pills that match the user's currently-active tags. */
    var tagCoMap = null;          /* { tag: Set(co-occurring tags), 'ALL': Set(all tags present) } */
    var allProductTagSets = null; /* Array of Set(tags) per product, used for AND-checks */

    function buildCoMap(products) {
      var coMap = { ALL: new Set() };
      var sets = [];
      products.forEach(function (p) {
        var raw = Array.isArray(p.tags) ? p.tags : (p.tags || '').split(',');
        var tagSet = new Set(raw.map(function (s) { return String(s).trim().toLowerCase(); }).filter(Boolean));
        sets.push(tagSet);
        tagSet.forEach(function (t) { coMap.ALL.add(t); });
        tagSet.forEach(function (t) {
          if (!coMap[t]) coMap[t] = new Set();
          tagSet.forEach(function (other) { if (other !== t) coMap[t].add(other); });
        });
      });
      tagCoMap = coMap;
      allProductTagSets = sets;
    }

    function loadCoMap() {
      if (tagCoMap || !collectionUrl) return Promise.resolve();
      return fetch(collectionUrl + '/products.json?limit=250')
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (j) { buildCoMap(j.products || []); applyConstraints(); })
        .catch(function () { /* silent — fall back to no dynamic disabling */ });
    }

    /* Returns true if there exists at least one product whose tag set
       contains every tag in `requiredTags`. */
    function anyProductHasAll(requiredTags) {
      if (!allProductTagSets) return true;
      for (var i = 0; i < allProductTagSets.length; i++) {
        var s = allProductTagSets[i];
        var ok = true;
        for (var j = 0; j < requiredTags.length; j++) {
          if (!s.has(requiredTags[j])) { ok = false; break; }
        }
        if (ok) return true;
      }
      return false;
    }

    function applyConstraints() {
      var disabled = getDisabledTags();
      var hasActive = state.tags.length > 0;
      root.querySelectorAll('[data-ef-pill="secondary"]').forEach(function (p) {
        var tag = (p.getAttribute('data-tag') || '').toLowerCase();
        var isOn = p.classList.contains('is-on');
        var isDisabled = !!disabled[tag];

        /* Dynamic 0-result check (only when we have product data + active filters
           + this pill isn't already selected). */
        if (!isDisabled && !isOn && hasActive && allProductTagSets) {
          var probe = state.tags.concat([tag]);
          if (!anyProductHasAll(probe)) isDisabled = true;
        }

        p.classList.toggle('ef-cv2__pill--disabled', isDisabled);
        p.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
        if (isDisabled && isOn) {
          p.classList.remove('is-on');
          p.setAttribute('aria-pressed', 'false');
          var idx = state.tags.indexOf(tag);
          if (idx !== -1) state.tags.splice(idx, 1);
        }
      });
    }

    /* Defer the 250-product constraint-map fetch until the browser is idle.
       This keeps it from competing with above-the-fold image bandwidth on
       initial page load. Filter pills work without the map; constraints just
       enable a moment later. */
    if (window.requestIdleCallback) {
      requestIdleCallback(loadCoMap, { timeout: 2000 });
    } else {
      setTimeout(loadCoMap, 800);
    }

    /* ─── Secondary pills (multi-select) ─── */
    root.addEventListener('click', function (e) {
      var pill = e.target.closest('[data-ef-pill="secondary"]');
      if (!pill) return;
      if (pill.classList.contains('ef-cv2__pill--disabled')) return;
      var tag = pill.getAttribute('data-tag');
      if (!tag) return;
      var idx = state.tags.indexOf(tag);
      if (idx === -1) state.tags.push(tag); else state.tags.splice(idx, 1);
      applyConstraints();
      ajaxLoad();
    });

    /* ─── Sort dropdown ─── */
    var sortWrap = root.querySelector('[data-ef-sort]');
    if (sortWrap) {
      var sortBtn = sortWrap.querySelector('[data-ef-sort-btn]');
      var sortMenu = sortWrap.querySelector('[data-ef-sort-menu]');
      sortBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        var open = sortWrap.classList.toggle('is-open');
        sortBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      document.addEventListener('click', function (e) {
        if (!sortWrap.contains(e.target)) {
          sortWrap.classList.remove('is-open');
          sortBtn.setAttribute('aria-expanded', 'false');
        }
      });
      sortWrap.querySelectorAll('[data-ef-sort-opt]').forEach(function (opt) {
        opt.addEventListener('click', function () {
          state.sort = opt.getAttribute('data-ef-sort-opt');
          sortWrap.querySelector('[data-ef-sort-current]').textContent = opt.textContent;
          sortWrap.classList.remove('is-open');
          ajaxLoad();
        });
      });
    }

    /* ─── Filter drawer ─── */
    var drawer = root.querySelector('[data-ef-drawer]');
    var lastFocus = null;
    function openDrawer() {
      if (!drawer) return;
      lastFocus = document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      var firstFocus = drawer.querySelector('.ef-cv2__drawer-close');
      if (firstFocus) setTimeout(function () { firstFocus.focus(); }, 50);
    }
    function closeDrawer() {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (lastFocus) lastFocus.focus();
    }
    root.querySelectorAll('[data-ef-open-drawer]').forEach(function (btn) {
      btn.addEventListener('click', openDrawer);
    });
    if (drawer) {
      drawer.querySelectorAll('[data-ef-drawer-close]').forEach(function (btn) {
        btn.addEventListener('click', closeDrawer);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
      });
      /* Focus trap */
      drawer.addEventListener('keydown', function (e) {
        if (e.key !== 'Tab') return;
        var focusables = drawer.querySelectorAll('button, [href], input, select, [tabindex]:not([tabindex="-1"])');
        if (!focusables.length) return;
        var first = focusables[0];
        var last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });

      /* Live result count in Apply button */
      var countSpan = drawer.querySelector('[data-ef-drawer-count]');
      var countTimer = null;
      function collectDrawerTags() {
        var tags = [];
        drawer.querySelectorAll('[data-ef-drawer-tag]').forEach(function (el) {
          var tag = el.getAttribute('data-ef-drawer-tag');
          var on = false;
          if (el.matches('input')) on = el.checked;
          else if (el.classList.contains('ef-cv2__swatch')) on = el.getAttribute('aria-pressed') === 'true';
          else if (el.classList.contains('ef-cv2__drawer-chip')) on = el.classList.contains('is-on');
          if (on && tag) tags.push(tag);
        });
        return tags;
      }
      function updateDrawerCount() {
        if (!countSpan) return;
        clearTimeout(countTimer);
        countTimer = setTimeout(function () {
          var tags = collectDrawerTags();
          var path = collectionUrl;
          if (tags.length) path += '/' + tags.join('+');
          var fetchUrl = new URL(path, window.location.origin);
          fetchUrl.searchParams.set('section_id', sectionId);
          var activePrice = drawer.querySelector('.ef-cv2__drawer-chip[data-ef-price-max].is-on, .ef-cv2__drawer-chip[data-ef-price-min].is-on');
          if (activePrice) {
            var pM = activePrice.getAttribute('data-ef-price-max');
            var pm = activePrice.getAttribute('data-ef-price-min');
            if (pM) fetchUrl.searchParams.set('filter.v.price.lte', pM);
            if (pm) fetchUrl.searchParams.set('filter.v.price.gte', pm);
          }
          countSpan.textContent = '(...)';
          fetch(fetchUrl.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
            .then(function (r) { return r.text(); })
            .then(function (html) {
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var cards = doc.querySelectorAll('[data-ef-cv2-grid] > *:not(.ef-cv2__skeleton)');
              var pagLinks = doc.querySelectorAll('.ef-cv2__pag a[data-ef-page]');
              var total = cards.length;
              if (pagLinks.length) {
                var countEl = doc.querySelector('[data-ef-cv2-total]');
                if (countEl) total = parseInt(countEl.textContent, 10) || total;
                else total = total + '+';
              }
              countSpan.textContent = '(' + total + ' results)';
            })
            .catch(function () {
              countSpan.textContent = '';
            });
        }, 300);
      }

      /* Color swatches toggle */
      drawer.querySelectorAll('.ef-cv2__swatch').forEach(function (s) {
        s.addEventListener('click', function () {
          var on = s.getAttribute('aria-pressed') === 'true';
          s.setAttribute('aria-pressed', on ? 'false' : 'true');
          updateDrawerCount();
        });
      });

      /* Drawer chips toggle */
      drawer.querySelectorAll('.ef-cv2__drawer-chip').forEach(function (chip) {
        chip.addEventListener('click', function () {
          var isPrice = chip.hasAttribute('data-ef-price-max') || chip.hasAttribute('data-ef-price-min');
          if (isPrice) {
            var wasOn = chip.classList.contains('is-on');
            drawer.querySelectorAll('.ef-cv2__drawer-chip[data-ef-price-max], .ef-cv2__drawer-chip[data-ef-price-min]').forEach(function (c) { c.classList.remove('is-on'); });
            if (!wasOn) chip.classList.add('is-on');
          } else {
            chip.classList.toggle('is-on');
          }
          updateDrawerCount();
        });
      });

      /* Checkboxes live count */
      drawer.querySelectorAll('input[type="checkbox"][data-ef-drawer-tag]').forEach(function (cb) {
        cb.addEventListener('change', updateDrawerCount);
      });

      /* Price range dual slider */
      var pmin = drawer.querySelector('[data-ef-price-min]');
      var pmax = drawer.querySelector('[data-ef-price-max]');
      var pminLabel = drawer.querySelector('[data-ef-price-min-label]');
      var pmaxLabel = drawer.querySelector('[data-ef-price-max-label]');
      var pfill = drawer.querySelector('[data-ef-price-fill]');
      function paintRange() {
        if (!pmin || !pmax) return;
        var lo = parseInt(pmin.value, 10);
        var hi = parseInt(pmax.value, 10);
        if (lo > hi - 50) { lo = hi - 50; pmin.value = lo; }
        var max = parseInt(pmin.max, 10);
        if (pfill) {
          pfill.style.left = (lo / max * 100) + '%';
          pfill.style.right = ((max - hi) / max * 100) + '%';
        }
        if (pminLabel) pminLabel.textContent = '$' + lo.toLocaleString();
        if (pmaxLabel) pmaxLabel.textContent = '$' + hi.toLocaleString();
      }
      if (pmin) pmin.addEventListener('input', paintRange);
      if (pmax) pmax.addEventListener('input', paintRange);
      paintRange();

      /* Collect price from active chip */
      function collectPrice() {
        state.priceMin = null;
        state.priceMax = null;
        var activePrice = drawer.querySelector('.ef-cv2__drawer-chip[data-ef-price-max].is-on, .ef-cv2__drawer-chip[data-ef-price-min].is-on');
        if (activePrice) {
          var pMaxAttr = activePrice.getAttribute('data-ef-price-max');
          var pMinAttr = activePrice.getAttribute('data-ef-price-min');
          if (pMaxAttr) state.priceMax = parseInt(pMaxAttr, 10);
          if (pMinAttr) state.priceMin = parseInt(pMinAttr, 10);
        }
      }

      /* Apply: collect tags from checkboxes, swatches, and chips + price */
      var applyBtn = drawer.querySelector('[data-ef-drawer-apply]');
      if (applyBtn) {
        applyBtn.addEventListener('click', function () {
          var newTags = [];
          drawer.querySelectorAll('[data-ef-drawer-tag]').forEach(function (el) {
            var tag = el.getAttribute('data-ef-drawer-tag');
            var on = false;
            if (el.matches('input')) on = el.checked;
            else if (el.classList.contains('ef-cv2__swatch')) on = el.getAttribute('aria-pressed') === 'true';
            else if (el.classList.contains('ef-cv2__drawer-chip')) on = el.classList.contains('is-on');
            if (on && tag) newTags.push(tag);
          });
          state.tags = newTags;
          collectPrice();
          closeDrawer();
          ajaxLoad();
        });
      }

      /* Clear all */
      var clearBtn = drawer.querySelector('[data-ef-drawer-clear]');
      if (clearBtn) {
        clearBtn.addEventListener('click', function () {
          drawer.querySelectorAll('input[type="checkbox"]').forEach(function (c) { c.checked = false; });
          drawer.querySelectorAll('.ef-cv2__swatch').forEach(function (s) { s.setAttribute('aria-pressed', 'false'); });
          drawer.querySelectorAll('.ef-cv2__drawer-chip').forEach(function (c) { c.classList.remove('is-on'); });
          if (pmin) { pmin.value = pmin.min; }
          if (pmax) { pmax.value = pmax.max; }
          paintRange();
          state.tags = [];
          state.priceMin = null;
          state.priceMax = null;
          closeDrawer();
          ajaxLoad();
        });
      }
    }

    /* ─── Pagination AJAX ─── */
    root.addEventListener('click', function (e) {
      var link = e.target.closest('[data-ef-page]');
      if (!link) return;
      e.preventDefault();
      var url = new URL(link.href, window.location.origin);
      ajaxLoad({ url: url, scrollToGrid: true });
    });

    /* ─── AJAX loader (Shopify Section Rendering API) ─── */
    function buildUrl() {
      var path = collectionUrl;
      if (state.tags.length) path += '/' + state.tags.join('+');
      var qs = [];
      if (state.sort) qs.push('sort_by=' + encodeURIComponent(state.sort));
      if (state.priceMin !== null) qs.push('filter.v.price.gte=' + state.priceMin);
      if (state.priceMax !== null) qs.push('filter.v.price.lte=' + state.priceMax);
      return path + (qs.length ? '?' + qs.join('&') : '');
    }

    function showSkeleton() {
      grid = root.querySelector('[data-ef-cv2-grid]');
      if (!grid || !skeletonTpl) return;
      var count = grid.children.length || 6;
      grid.innerHTML = '';
      for (var i = 0; i < Math.min(count, 8); i++) {
        grid.appendChild(skeletonTpl.content.cloneNode(true));
      }
    }

    function ajaxLoad(opts) {
      opts = opts || {};
      var pretty = opts.url ? opts.url.toString() : buildUrl();
      var fetchUrl = new URL(pretty, window.location.origin);
      fetchUrl.searchParams.set('section_id', sectionId);

      /* Lock grid height before replacing with skeletons to prevent page jump */
      var gridWrap = root.querySelector('.ef-cv2__grid-wrap');
      if (gridWrap) gridWrap.style.minHeight = gridWrap.offsetHeight + 'px';

      /* Scroll to top of section BEFORE skeleton swap */
      if (opts.scrollToGrid) {
        var sectionEl = root.closest('.shopify-section') || root;
        var headerH = 0;
        var headerEl = document.querySelector('#shopify-section-header, .shopify-section--header');
        if (headerEl) headerH = headerEl.offsetHeight || 0;
        var top = sectionEl.offsetTop - headerH;
        window.scrollTo(0, Math.max(0, top));
      }

      /* Fade out current grid, then show skeletons */
      if (gridWrap) {
        gridWrap.style.transition = 'opacity 200ms ease';
        gridWrap.style.opacity = '0';
      }

      setTimeout(function() {
        if (gridWrap) gridWrap.style.opacity = '1';
        showSkeleton();

      fetch(fetchUrl.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          /* Shopify Section Rendering returns the full section markup wrapped in
             #shopify-section-{id}. Find new section root + new grid + new pagination. */
          var newRoot = doc.querySelector('[data-ef-cv2]') || doc.querySelector('#shopify-section-' + sectionId + ' [data-ef-cv2]');
          if (!newRoot) {
            window.location.href = pretty;
            return;
          }
          /* Hide grid wrap BEFORE swapping DOM to prevent flash */
          var gw = root.querySelector('.ef-cv2__grid-wrap');
          if (gw) {
            gw.style.transition = 'none';
            gw.style.opacity = '0';
          }

          var newGrid = newRoot.querySelector('[data-ef-cv2-grid]');
          var oldGrid = root.querySelector('[data-ef-cv2-grid]');
          if (newGrid && oldGrid) oldGrid.replaceWith(newGrid);
          grid = root.querySelector('[data-ef-cv2-grid]');

          /* Replace pagination (or remove if none) */
          var newPag = newRoot.querySelector('.ef-cv2__pag');
          var oldPag = root.querySelector('.ef-cv2__pag');
          if (newPag && oldPag) oldPag.replaceWith(newPag);
          else if (newPag && !oldPag) {
            var wrap = root.querySelector('.ef-cv2__grid-wrap');
            if (wrap) wrap.appendChild(newPag);
          } else if (!newPag && oldPag) oldPag.remove();

          /* If no products → swap whole grid-wrap (covers the empty state) */
          var newEmpty = newRoot.querySelector('.ef-cv2__empty');
          var oldEmpty = root.querySelector('.ef-cv2__empty');
          var oldWrap = root.querySelector('.ef-cv2__grid-wrap');
          if (newEmpty && !oldEmpty) {
            var newWrap = newRoot.querySelector('.ef-cv2__grid-wrap');
            if (newWrap && oldWrap) oldWrap.replaceWith(newWrap);
          }
          if (oldEmpty && !newEmpty) {
            var newWrap2 = newRoot.querySelector('.ef-cv2__grid-wrap');
            if (newWrap2 && oldWrap) oldWrap.replaceWith(newWrap2);
          }

          /* Sync URL */
          history.pushState({}, '', pretty);

          /* Re-paint state on the new DOM */
          paintWishlist(root);
          paintActivePills();
          applyDisclosure(currentCategory(), false);

          /* Release locked height */
          gw = root.querySelector('.ef-cv2__grid-wrap');
          if (gw) gw.style.minHeight = '';

          /* Pre-hide all new cards before making grid visible */
          var cards = root.querySelectorAll('.ef-cv2__card');
          cards.forEach(function(card) {
            card.style.opacity = '0';
            card.style.transform = 'translateY(16px)';
          });

          /* Make grid visible, then stagger cards in */
          requestAnimationFrame(function() {
            if (gw) {
              gw.style.transition = 'none';
              gw.style.opacity = '1';
            }
            requestAnimationFrame(function() {
              cards.forEach(function(card, i) {
                card.style.transition = 'opacity 350ms ease, transform 350ms ease';
                card.style.transitionDelay = (i * 40) + 'ms';
                card.style.opacity = '1';
                card.style.transform = 'translateY(0)';
              });
            });
          });
        })
        .catch(function () {
          /* Network failure → hard navigate */
          window.location.href = pretty;
        });
      }, 200);
    }

    function paintActivePills() {
      root.querySelectorAll('[data-ef-pill="primary"]').forEach(function (p) {
        var t = p.getAttribute('data-tag') || '';
        var on = (t === '' && state.tags.length === 0) || (t && state.tags.indexOf(t) !== -1);
        p.classList.toggle('is-on', on);
        p.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      root.querySelectorAll('[data-ef-pill="secondary"]').forEach(function (p) {
        var t = p.getAttribute('data-tag');
        var on = state.tags.indexOf(t) !== -1;
        p.classList.toggle('is-on', on);
        p.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      applyConstraints();
    }

    /* Browser back/forward → reload from URL */
    window.addEventListener('popstate', function () {
      window.location.reload();
    });

    /* Ensure All Filters button exists in bar-right */
    var barRight = root.querySelector('.ef-cv2__bar-right');
    if (barRight && !barRight.querySelector('.ef-cv2__filter-btn')) {
      var fb = document.createElement('button');
      fb.type = 'button';
      fb.className = 'ef-cv2__filter-btn';
      fb.setAttribute('data-ef-open-drawer', '');
      fb.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" width="14" height="14"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/><circle cx="8" cy="6" r="2" fill="currentColor" stroke="currentColor" stroke-width="1.2"/><circle cx="16" cy="12" r="2" fill="currentColor" stroke="currentColor" stroke-width="1.2"/><circle cx="6" cy="18" r="2" fill="currentColor" stroke="currentColor" stroke-width="1.2"/></svg> All Filters';
      barRight.insertBefore(fb, barRight.firstChild);
      fb.addEventListener('click', openDrawer);
    }
  }
})();

/* ── Family swatch interaction on product cards ──
   - Hover swatch: preview-swap main image (temporary)
   - Click swatch: pin-swap (image + active ring + parent link href all update)
   - Mouseleave: revert to currently-pinned image (NOT original)
   Lives globally so it works on collection grid, related products,
   bestsellers — anywhere ef-cv2-card / ef-cv2__card is rendered. */
(function () {
  function getMainImg(card) {
    return card.querySelector('.ef-cv2__card-img:not(.ef-cv2__card-img--hover)');
  }
  function bind(box) {
    if (box.__efSwatchBound) return;
    box.__efSwatchBound = true;
    var card = box.closest('.ef-cv2__card');
    if (!card) return;
    var img = getMainImg(card);
    if (!img) return;

    /* Mutable "pinned" state — initial = current src, updated on click */
    card.__efPinSrc = img.getAttribute('src');
    card.__efPinSrcset = img.getAttribute('srcset');

    box.addEventListener('mouseover', function (e) {
      var sw = e.target.closest('.ef-cv2__card-swatch');
      if (!sw || !sw.dataset.img) return;
      img.setAttribute('src', sw.dataset.img);
      img.removeAttribute('srcset');
      card.classList.add('ef-cv2__card--swatch-active');
    });
    box.addEventListener('mouseleave', function () {
      img.setAttribute('src', card.__efPinSrc);
      if (card.__efPinSrcset) img.setAttribute('srcset', card.__efPinSrcset);
      else img.removeAttribute('srcset');
      card.classList.remove('ef-cv2__card--swatch-active');
    });
  }
  function bindAll() {
    document.querySelectorAll('.ef-cv2__card-swatches').forEach(bind);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAll);
  } else {
    bindAll();
  }
  document.addEventListener('ef:grid-updated', bindAll);
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-ef-pill]')) setTimeout(bindAll, 600);
  });

  /* 2026-06-24 — Buying-Guide "collection mode": a quiz result lands here as
     ?size=24&vent=vented. Pre-select that size on every gas-log family card (swaps
     price/img/link via efCardSwatchClick) and HIDE families that don't offer it, so the
     shopper sees exactly "all sets in my size". A banner explains + lets them clear it. */
  (function () {
    var params = new URLSearchParams(location.search);
    var size = params.get('size');
    if (!size || !/^\d+$/.test(size)) return;
    var vent = params.get('vent');
    function applySizeFilter() {
      var cards = document.querySelectorAll('.ef-cv2__card');
      if (!cards.length) return;
      var shown = 0, anyGasCard = false;
      cards.forEach(function (card) {
        var swatches = card.querySelectorAll('.efg-card-sizes .ef-cv2__card-swatch, .efg-card-sizes .efg-size-pill');
        if (!swatches.length) return; /* not a sized gas-log family card — leave untouched */
        anyGasCard = true;
        var match = null;
        swatches.forEach(function (s) { var n = (s.textContent || '').match(/\d+/); if (n && n[0] === String(size)) match = s; });
        if (match) {
          if (!match.classList.contains('is-active') && typeof window.efCardSwatchClick === 'function') window.efCardSwatchClick(match);
          card.style.display = ''; shown++;
        } else {
          card.style.display = 'none';
        }
      });
      if (anyGasCard) showBanner(shown);
    }
    function showBanner(count) {
      var existing = document.getElementById('ef-gq-collbanner');
      if (existing) { existing.querySelector('[data-gq-count]').textContent = count + ' set' + (count === 1 ? '' : 's'); return; }
      var ventName = vent === 'ventless' ? 'Ventless' : (vent === 'vented' ? 'Vented' : '');
      var b = document.createElement('div');
      b.id = 'ef-gq-collbanner';
      b.style.cssText = 'display:flex;align-items:center;gap:14px;justify-content:center;flex-wrap:wrap;margin:0 0 18px;padding:12px 20px;background:linear-gradient(135deg,#26211B,#15120E);color:#FBF7F1;border-radius:12px;font:600 13.5px/1.4 Inter,-apple-system,sans-serif;box-shadow:0 2px 10px rgba(20,18,12,0.18);';
      b.innerHTML = '<span>Your match: <strong style="color:#E7C79A;">' + size + '&quot;' + (ventName ? ' ' + ventName : '') + '</strong> &middot; <span data-gq-count>' + count + ' set' + (count === 1 ? '' : 's') + '</span></span>' +
        '<a href="' + location.pathname + '" style="color:#D9B381;text-decoration:underline;font-weight:600;font-size:12.5px;white-space:nowrap;">Clear &times;</a>';
      var firstCard = document.querySelector('.ef-cv2__card');
      var grid = firstCard && firstCard.parentElement;
      if (grid && grid.parentNode) grid.parentNode.insertBefore(b, grid);
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { setTimeout(applySizeFilter, 350); });
    else setTimeout(applySizeFilter, 350);
    document.addEventListener('ef:grid-updated', function () { setTimeout(applySizeFilter, 200); });
  })();

  /* Click handler — pinned swap, exposed globally for inline onclick */
  window.efCardSwatchClick = function (btn) {
    var card = btn.closest('.ef-cv2__card');
    if (!card) return;
    var img = getMainImg(card);
    if (img && btn.dataset.img) {
      img.setAttribute('src', btn.dataset.img);
      img.removeAttribute('srcset');
      /* Update pinned state so mouseleave reverts to THIS, not original */
      card.__efPinSrc = btn.dataset.img;
      card.__efPinSrcset = '';
    }
    /* Update active ring */
    card.querySelectorAll('.ef-cv2__card-swatch').forEach(function (s) {
      var on = (s === btn);
      s.classList.toggle('is-active', on);
      s.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    /* Swap the secondary (hover) image to match the selected finish.
       If the family member has no 2nd photo, hide the hover image. */
    var hoverImg = card.querySelector('.ef-cv2__card-img--hover');
    if (hoverImg) {
      if (btn.dataset.img2) {
        hoverImg.setAttribute('src', btn.dataset.img2);
        hoverImg.removeAttribute('srcset');
        card.classList.remove('ef-cv2__card--family-no-hover');
      } else {
        card.classList.add('ef-cv2__card--family-no-hover');
      }
    }
    /* Update card link → click anywhere on card now navigates to selected finish */
    var link = card.querySelector('.ef-cv2__card-link');
    if (link && btn.dataset.href) link.setAttribute('href', btn.dataset.href);
    /* Update card title (size pills carry their target product's title). */
    if (btn.dataset.title) {
      var titleEl = card.querySelector('.ef-cv2__card-title');
      if (titleEl) titleEl.textContent = btn.dataset.title;
      /* Also update <a> wrapping the title if it has one */
      var titleLink = card.querySelector('.ef-cv2__card-title-link');
      if (titleLink && btn.dataset.href) titleLink.setAttribute('href', btn.dataset.href);
    }
    /* Update card price (size pills carry their target product's price). */
    if (btn.dataset.price) {
      var priceEl = card.querySelector('.ef-cv2__card-price');
      if (priceEl) {
        /* EFGL-SIZEDEDUP-2026-08-17: FROM budowane z danych, nie z DOM — kopiowanie istniejącego
           elementu gubiło FROM na zawsze po pierwszym kliknięciu bez niego. */
        var fromHTML = (btn.dataset.priceVaries === '1') ? '<span class="ef-cv2__card-price-from">From</span>' : '';
        priceEl.innerHTML = fromHTML + btn.dataset.price;
      }
    }
    /* Update AR/3D button to point to the selected family member */
    var arBtn = card.querySelector('[data-ar-url]');
    if (arBtn && btn.dataset.href) {
      arBtn.setAttribute('data-ar-url', btn.dataset.href + '?view=ar');
    }
    /* Update card-level state (used by quickview/ATC) */
    if (btn.dataset.handle) card.dataset.currentHandle = btn.dataset.handle;
    if (btn.dataset.href) card.dataset.currentHref = btn.dataset.href;
    /* Notify listeners (quickview can update its target) */
    card.dispatchEvent(new CustomEvent('ef:family-selected', {
      bubbles: true,
      detail: { handle: btn.dataset.handle, href: btn.dataset.href, img: btn.dataset.img }
    }));
  };
})();
